// ============================================================
//  Baseline-Validierung: Woraus besteht der heutige FUE-Bedarf, und wie weit liegt er über dem
//  Vertrag? Jeder Baustein ist aus den Personen gerechnet und hinterlegt die Personen dahinter —
//  keine Erzählschritte, keine skalierten Zahlen. Die Bausteine sind disjunkte FUE-Anteile und
//  summieren exakt auf den ausgewiesenen Bedarf (Kontrolle in der Oberfläche und im Test).
// ============================================================
import type { Account, Person } from "./types";
import type { Dataset } from "../data/generator";
import { DEFAULT_RATIO, type RatioTable, weight } from "./rules";
import { contractedFue } from "../store/contract";

export const KARTEILEICHE_TAGE = 90;

export type BaselineKey = "grund" | "extern" | "karteileiche" | "gesperrt" | "entwickler" | "ueber" | "identitaet";

export interface BaselinePersonRow {
  id: string; name?: string; type: string; unit: string; department?: string;
  cls: string; cleanCls: string; fue: number; lastLogonDays?: number;
}

export interface BaselineComponent {
  key: BaselineKey;
  label: string;
  /** FUE-Anteil dieses Bausteins am heutigen Bedarf. */
  fue: number;
  /** Personen, die zu diesem Baustein beitragen (leer bei „identitaet"). */
  rows: BaselinePersonRow[];
  /** Ein Satz, was der Baustein bedeutet und wie er gerechnet ist. */
  erklaerung: string;
  /** Wo man weiter nachsieht, wenn die Liste nicht reicht. */
  link?: { to: string; label: string };
}

export interface BaselineResult {
  contract: number;
  components: BaselineComponent[];
  /** Σ der Bausteine (= Bedarf heute unter dem aktiven Schlüssel). */
  total: number;
  /** Bedarf über echte Personen (Schlüssel Personalnummer). */
  totalTrue: number;
  /** Personen ohne FUE-System (nur GSP/HCM) — Information, kein Anteil am Bedarf. */
  ohneFue: { persons: number; rows: BaselinePersonRow[] };
}

function row(p: Person, fue: number): BaselinePersonRow {
  return { id: p.id, name: p.name, type: p.type, unit: p.unit, department: p.department, cls: p.licenseClass, cleanCls: p.cleanClass, fue, lastLogonDays: p.lastLogonDays };
}
const byFueDesc = (a: BaselinePersonRow, b: BaselinePersonRow) => b.fue - a.fue || a.id.localeCompare(b.id);

/**
 * @param consolidatedFue Bedarf unter dem aktiven Identitätsschlüssel (Cockpit-Zahl). Die Differenz zum
 *   Bedarf über echte Personen ist der Baustein „Identitätsauflösung".
 */
export function baselineComponents(ds: Dataset, ratio: RatioTable = DEFAULT_RATIO, consolidatedFue?: number, identityKeyLabel = "Personalnummer"): BaselineResult {
  const accById = new Map<string, Account>(ds.accounts.map((a) => [a.id, a]));
  const premium = Math.max(0, weight("Developer", ratio) - weight("Advanced", ratio));
  const grund: BaselinePersonRow[] = [], extern: BaselinePersonRow[] = [], kartei: BaselinePersonRow[] = [], gesperrt: BaselinePersonRow[] = [];
  const dev: BaselinePersonRow[] = [], ueber: BaselinePersonRow[] = [], ohne: BaselinePersonRow[] = [];
  let sGrund = 0, sExtern = 0, sKartei = 0, sGesperrt = 0, sDev = 0, sUeber = 0, totalTrue = 0;
  for (const p of ds.persons) {
    const ist = weight(p.licenseClass, ratio);
    if (p.licenseClass === "NotRelevant") { if (p.accountIds.length) ohne.push(row(p, 0)); continue; }
    totalTrue += ist;
    const clean = weight(p.cleanClass, ratio);
    const over = Math.max(0, ist - clean);
    const prem = p.cleanClass === "Developer" ? premium : 0;
    const base = clean - prem;
    if (over > 0) { ueber.push(row(p, over)); sUeber += over; }
    if (prem > 0) { dev.push(row(p, prem)); sDev += prem; }
    const accs = p.accountIds.map((id) => accById.get(id)).filter((a): a is Account => !!a);
    const locked = accs.length > 0 && accs.every((a) => a.locked);
    const karteileiche = !locked && (p.lastLogonDays ?? 0) > KARTEILEICHE_TAGE;
    if (locked) { gesperrt.push(row(p, base)); sGesperrt += base; }
    else if (karteileiche) { kartei.push(row(p, base)); sKartei += base; }
    else if (p.type === "extern") { extern.push(row(p, base)); sExtern += base; }
    else { grund.push(row(p, base)); sGrund += base; }
  }
  for (const l of [grund, extern, kartei, gesperrt, dev, ueber]) l.sort(byFueDesc);
  const total = consolidatedFue ?? totalTrue;
  const identitaet = total - totalTrue;
  const components: BaselineComponent[] = [
    { key: "grund", label: "Grundbedarf aktiver interner Personen", fue: sGrund, rows: grund, erklaerung: "Zielklasse (nach Rückbau der entfernbaren Rollen) aller Personen, die weder extern noch gesperrt noch länger als 90 Tage ohne Anmeldung sind. Entwickler zählen hier mit dem Gewicht von Advanced; der Aufschlag steht getrennt.", link: { to: "/personen", label: "Personenübersicht" } },
    { key: "extern", label: "Externe", fue: sExtern, rows: extern, erklaerung: "Zielklasse aller externen Personen (Berater, befristet); ihre Über-Berechtigung steht im Baustein Über-Berechtigung. Bei Vertragsschluss typischerweise nicht oder nur pauschal berücksichtigt.", link: { to: "/personen?type=extern", label: "Personen · Filter Extern" } },
    { key: "karteileiche", label: `Karteileichen (> ${KARTEILEICHE_TAGE} Tage ohne Anmeldung)`, fue: sKartei, rows: kartei, erklaerung: `Zielklasse aller Personen, deren letzte Anmeldung auf allen Konten länger als ${KARTEILEICHE_TAGE} Tage zurückliegt; ihre Über-Berechtigung steht im Baustein Über-Berechtigung. Gültigkeit beenden oder löschen senkt den Bedarf; Sperren allein nicht (PCE zählt gesperrte Konten weiter).` },
    { key: "gesperrt", label: "Gesperrte Personen (alle Konten gesperrt)", fue: sGesperrt, rows: gesperrt, erklaerung: "Zielklasse aller Personen, deren Konten sämtlich gesperrt sind; ihre Über-Berechtigung steht im Baustein Über-Berechtigung. Sperren senkt den Bedarf nicht (PCE zählt gesperrte Konten weiter) — erst Gültigkeit beenden oder löschen nimmt sie aus der Zählung." },
    { key: "entwickler", label: "Entwickler-Aufschlag (Faktor 2 statt 1)", fue: sDev, rows: dev, erklaerung: `Developer wiegt ${weight("Developer", ratio)} statt ${weight("Advanced", ratio)} FUE. Der Aufschlag je Entwickler steht hier getrennt, damit sichtbar ist, was der Faktor kostet.` },
    { key: "ueber", label: "Vermeidbare Über-Berechtigung", fue: sUeber, rows: ueber, erklaerung: "Differenz zwischen heutiger Klasse und Zielklasse je Person — der Teil des Bedarfs, den die Maßnahmen der Optimierung abbauen können (Zielzustand, R4).", link: { to: "/optimierung", label: "Optimierung" } },
  ];
  if (Math.round(identitaet) !== 0) components.push({ key: "identitaet", label: `Identitätsauflösung: ${identityKeyLabel}`, fue: identitaet, rows: [], erklaerung: identitaet > 0 ? `Der Schlüssel ${identityKeyLabel} führt Konten derselben Person nicht vollständig zusammen; die Mehrfachzählung erhöht den Bedarf gegenüber der Personalnummer. Umschaltbar in den Einstellungen.` : `Der Schlüssel ${identityKeyLabel} führt mehr Konten zusammen als die Personalnummer.`, link: { to: "/personen", label: "Personen · Wirkung des Identitätsschlüssels" } });
  return { contract: contractedFue(ratio), components, total, totalTrue, ohneFue: { persons: ohne.length, rows: ohne.sort((a, b) => a.id.localeCompare(b.id)) } };
}
